import { Button } from "@/components/ui/baseUi/button"
import { StandardInput } from "@/components/ui/forms/standardInput"
import { SwitchInlineInput } from "@/components/ui/forms/switchInlineInput"
import { ErrorView } from "@/components/ui/layout/error/errorView"
import { Text } from "@/components/ui/baseUi/text"
import { word } from "@/modules/core/utils/i18n"
import { getFormErrorMessage } from "@/modules/formik/formikUtils"
import { cn } from "@/modules/lib/utils"
import { SessionBlockType } from "@/modules/sessions/sessionDetails/sessionBlockTypes"
import { AddBlock } from "@/modules/sessions/sessionForm/blocks/components/addBlock"
import { ExistingBlock } from "@/modules/sessions/sessionForm/blocks/components/existingBlock"
import type { ExerciseFormikType } from "@/modules/sessions/sessionForm/blocks/exerciseBlock/exerciseFormikTypes"
import type { MediaBlockFormikType } from "@/modules/sessions/sessionForm/blocks/mediaBlock/mediaBlockFormikTypes"
import type {
  AthleteNoteFormikType,
  CoachNoteFormikType,
} from "@/modules/sessions/sessionForm/blocks/noteBlock/noteFormikTypes"
import { SessionTagEnum, SessionTagInput } from "@/modules/sessions/sessionForm/components/sessionTagInput"
import { useBlockDuration } from "@/modules/sessions/sessionForm/hooks/useBlockDuration"
import { useSessionSidebar } from "@/modules/sessions/sessionForm/hooks/useSessionSidebar"
import { useSidebar } from "@/modules/sidebar/hooks/useSidebar"
import type { TemplateSessionEntity } from "@/modules/template/entities/templateElementEntity"
import { useTemplateElement } from "@/modules/template/hooks/useTemplate"
import { useTemplateSessionEditFormik } from "@/modules/template/hooks/useTemplateFormik"
import { useTemplateExerciseImportSidebar } from "@/modules/template/hooks/useTemplateSidebar"
import { useConfirmPopup } from "@/modules/ui/components/popup/huConfirmPopup"
import { LoaderCircleIcon } from "lucide-react"
import { useEffect, useEffectEvent, useRef } from "react"

type TemplateSessionFormWrapperProps = {
  templateId: string
  className?: string
} & React.ComponentProps<"div">

export function TemplateSessionFormWrapper({ templateId, className, ...props }: TemplateSessionFormWrapperProps) {
  const { data: templateElement } = useTemplateElement(templateId)
  const session = "session" in templateElement ? templateElement.session : null

  if (!session) return <ErrorView />

  return <TemplateSessionEditForm templateId={templateId} session={session} className={className} {...props} />
}

type TemplateSessionEditFormProps = {
  templateId: string
  session?: TemplateSessionEntity
  sessionIndex?: number
  className?: string
} & React.ComponentProps<"div">

export function TemplateSessionEditForm({
  templateId,
  session,
  sessionIndex,
  className,
  ...props
}: TemplateSessionEditFormProps) {
  const { navigateToExerciseForm, navigateToNoteForCoachesForm, navigateToNoteForAthletesForm, navigateToMediaForm } =
    useSessionSidebar()
  const { navigateToTemplateExerciseImportView } = useTemplateExerciseImportSidebar()
  const { hideSidebarStackOnTop, setCurrentStackOnHide, hideSidebar } = useSidebar()
  const confirmPopup = useConfirmPopup()
  const containerRef = useRef<HTMLDivElement>(null)

  const { formik, isSubmitting } = useTemplateSessionEditFormik({
    templateId,
    session,
    sessionIndex,
    onSuccess: () => {
      hideSidebar(false)
    },
  })

  const { timedBlocks, timedBlocksTotal } = useBlockDuration(formik.values.blocks)

  const onSubmitSessionForm = async () => {
    Object.keys(formik.values).forEach((inputKey) => {
      void formik.setFieldTouched(inputKey)
    })
    const errors = await formik.validateForm()

    if (Object.keys(errors).length === 0) {
      await formik.submitForm()
    } else {
      const errorElements = containerRef.current?.querySelectorAll('[aria-invalid="true"]')
      if (errorElements && errorElements.length > 0) {
        document
          .querySelector('[data-slot="right-sidebar-content"]')
          ?.scrollTo({ top: (errorElements[0] as HTMLElement).offsetTop - 30, behavior: "smooth" })
      }
    }
  }

  const addExercise = () => {
    navigateToExerciseForm(undefined, formik.values.blocks.length, async (exercise: ExerciseFormikType) => {
      await formik.setFieldValue("blocks", [
        ...formik.values.blocks,
        { type: SessionBlockType.SESSION_EXERCISE, content: exercise },
      ])
      hideSidebarStackOnTop()
    })
  }

  const addCoachNote = () => {
    navigateToNoteForCoachesForm(undefined, formik.values.blocks.length, async (note: CoachNoteFormikType) => {
      await formik.setFieldValue("blocks", [
        ...formik.values.blocks,
        { type: SessionBlockType.COACH_NOTE, content: note },
      ])
      hideSidebarStackOnTop()
    })
  }

  const addAthleteNoteBlock = () => {
    navigateToNoteForAthletesForm(undefined, formik.values.blocks.length, async (note: AthleteNoteFormikType) => {
      await formik.setFieldValue("blocks", [
        ...formik.values.blocks,
        { type: SessionBlockType.ATHLETE_NOTE, content: note },
      ])
      hideSidebarStackOnTop()
    })
  }
  const addMediaBlock = () => {
    navigateToMediaForm(undefined, formik.values.blocks.length, async (mediaBlock: MediaBlockFormikType) => {
      await formik.setFieldValue("blocks", [
        ...formik.values.blocks,
        { type: SessionBlockType.MEDIAS, content: mediaBlock },
      ])
      hideSidebarStackOnTop()
    })
  }

  const addTemplateExercise = () => {
    navigateToTemplateExerciseImportView(formik.values.blocks.length, async (exercise: ExerciseFormikType) => {
      await formik.setFieldValue("blocks", [
        ...formik.values.blocks,
        { type: SessionBlockType.SESSION_EXERCISE, content: exercise },
      ])
      hideSidebarStackOnTop()
    })
  }

  const onHideSidebarWhenDirty = useEffectEvent(() => {
    setCurrentStackOnHide(async () => {
      if (formik.dirty) {
        const confirm = await new Promise<boolean>((resolve) => {
          confirmPopup.show({
            title: word("program.sidebar.closeMessage.title"),
            message: word("program.sidebar.closeMessage.message"),
            accept: () => {
              resolve(true)
              return Promise.resolve()
            },
            cancel: () => {
              resolve(false)
              return Promise.resolve()
            },
            footerProps: {
              confirmText: word("global.ok"),
            },
          })
        })

        return confirm
      } else {
        return true
      }
    })
  })

  useEffect(() => {
    onHideSidebarWhenDirty()
  }, [formik.dirty])

  return (
    <div ref={containerRef} className={cn("flex flex-1 flex-col gap-4 px-6 pt-18 pb-6", className)} {...props}>
      <Text fontStyle="TL">{word("template.title.text.sessionTemplate")}</Text>

      <div className="flex flex-1 flex-col justify-between gap-6">
        <div className="flex flex-col gap-5">
          {/* Session name */}
          <StandardInput
            isRequiredInput
            label={word("sessionForm.name.title")}
            name="name"
            getFieldProps={formik.getFieldProps}
            setFieldValue={formik.setFieldValue}
            error={getFormErrorMessage("name", formik)}
            inputProps={{ placeholder: word("sessionForm.name.title") }}
          />

          {/* Session tag selection */}
          <div className="flex flex-col gap-2">
            <Text fontStyle="LS" className="text-textDefault-body uppercase">
              {word("sessionForm.tag.title")}
            </Text>

            <div className="flex flex-row items-center justify-between">
              <SessionTagInput
                selectedTags={[
                  { key: SessionTagEnum.gymnasticTag, value: formik.values.gymnasticTag },
                  { key: SessionTagEnum.weightliftingTag, value: formik.values.weightliftingTag },
                  { key: SessionTagEnum.cardioTag, value: formik.values.cardioTag },
                ]}
                onChangeSelectedTags={(tag: string, value: boolean) => {
                  void formik.setFieldValue(tag, value)
                }}
              />
            </div>
          </div>

          {/* Blocks */}
          <div className="flex flex-col gap-2">
            <div className="flex flex-row items-center justify-between">
              <Text fontStyle="LS" className="text-textDefault-body uppercase">
                {`${word("program.planning.create.exercise.block_plural", { count: formik.values.blocks.length })} (${formik.values.blocks.length.toString()})`}
              </Text>

              {timedBlocks.length > 0 && (
                <div className="flex flex-row">
                  <Text fontStyle="LS" className="border-r border-borderDefault pr-1 text-textDefault-body uppercase">
                    {`${word("sessionForm.blocks.timed")} (${timedBlocks.length.toString()})`}
                  </Text>
                  <Text fontStyle="LS" className="pl-1 text-textDefault-body">
                    {word("sessionForm.block.totalTime")}
                    <Text fontStyle="LS" className="pl-1 font-bold">
                      {timedBlocksTotal}
                    </Text>
                    {word("global.minute.shortLabel", { count: timedBlocksTotal })}
                  </Text>
                </div>
              )}
            </div>

            {formik.values.blocks.map((block, i) => (
              <ExistingBlock formik={formik} block={block} blockIndex={i} key={i} />
            ))}

            {/* Add block */}
            <AddBlock
              onAddExerciseBlock={addExercise}
              onAddCoachNoteBlock={addCoachNote}
              onAddAthleteNoteBlock={addAthleteNoteBlock}
              onAddMediaBlock={addMediaBlock}
              onAddTemplateExercise={addTemplateExercise}
            />
          </div>

          {/* Hide session */}
          <SwitchInlineInput
            label={word("session.form.hideSessionContent.label")}
            name="hideSessionContent"
            isRequiredInput
            setFieldValue={formik.setFieldValue}
            getFieldProps={formik.getFieldProps}
            error={getFormErrorMessage("hideSessionContent", formik)}
            textSwitch={word("session.form.hideSessionContent.textSwitch")}
          >
            <Text fontStyle="BS" className="px-8 text-justify leading-5 text-textDefault-caption">
              {word("session.form.hideSessionContent.description")}
            </Text>
          </SwitchInlineInput>
        </div>

        <div className="flex flex-row justify-end gap-2">
          <Button
            variant="outline"
            size="lg"
            className="flex-1"
            disabled={isSubmitting}
            onClick={() => {
              hideSidebar()
            }}
          >
            {word("global.cancel")}
          </Button>
          <Button
            variant="primary"
            size="lg"
            className="flex-1"
            onClick={() => void onSubmitSessionForm()}
            disabled={isSubmitting || !formik.dirty}
            isLoading={isSubmitting}
          >
            {isSubmitting && <LoaderCircleIcon className="animate-spin" />}
            {word("global.save")}
          </Button>
        </div>
      </div>
    </div>
  )
}
